import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import CalendlyCTA from "@/components/CalendlyCTA";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

const COMMITMENT_OPTIONS = [
  { value: "exploring", label: "Just exploring" },
  { value: "ready", label: "Ready to begin" },
  { value: "committed", label: "Fully committed" },
];

export default function ApplyPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    current_challenges: "",
    goals: "",
    why_now: "",
    commitment_level: "",
    preferred_time: "",
    referral_source: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.commitment_level) {
      toast.error("Please select a commitment level.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/coaching/apply", form);
      setSubmitted(true);
      toast.success("Your application has been received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="apply-page" className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }}>
      <Navbar />

      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="apply-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] hover:text-[#C07865] transition-colors mb-12"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Return home
          </Link>

          {!submitted ? (
            <>
              <p className="overline mb-6">A quiet beginning</p>
              <h1 className="serif text-5xl md:text-6xl text-[#15243D] leading-[1.05] mb-8" style={{ fontWeight: 300 }}>
                Apply for <em className="italic text-[#C07865]">coaching</em>.
              </h1>
              <p className="text-lg font-light leading-relaxed text-[#5A5D63] mb-16 max-w-2xl">
                Take your time with these questions. There are no right answers
                — only honest ones. I read every application personally and
                respond within three days.
              </p>

              <form onSubmit={submit} className="space-y-14" data-testid="apply-form">
                {/* Basics */}
                <div className="space-y-8">
                  <p className="overline">Section I — Contact</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Full name
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={update("name")}
                        className="anchor-input"
                        data-testid="apply-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className="anchor-input"
                        data-testid="apply-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Phone <span className="text-[#8A8D93] normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        value={form.phone}
                        onChange={update("phone")}
                        className="anchor-input"
                        data-testid="apply-phone-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                        Preferred meeting time <span className="text-[#8A8D93] normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        placeholder="e.g. Tuesday mornings, EST"
                        value={form.preferred_time}
                        onChange={update("preferred_time")}
                        className="anchor-input"
                        data-testid="apply-time-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Reflection */}
                <div className="space-y-10">
                  <p className="overline">Section II — Where you are</p>

                  <div>
                    <label className="serif text-xl text-[#15243D] mb-3 block leading-snug" style={{ fontWeight: 300 }}>
                      What are you currently navigating?
                    </label>
                    <p className="text-sm font-light text-[#8A8D93] mb-3">
                      What’s heavy, stuck, or unresolved right now.
                    </p>
                    <textarea
                      required
                      rows={5}
                      value={form.current_challenges}
                      onChange={update("current_challenges")}
                      className="anchor-input"
                      data-testid="apply-challenges-input"
                    />
                  </div>

                  <div>
                    <label className="serif text-xl text-[#15243D] mb-3 block leading-snug" style={{ fontWeight: 300 }}>
                      What would the next chapter look like?
                    </label>
                    <p className="text-sm font-light text-[#8A8D93] mb-3">
                      Not goals — a quality of life. What would feel different?
                    </p>
                    <textarea
                      required
                      rows={5}
                      value={form.goals}
                      onChange={update("goals")}
                      className="anchor-input"
                      data-testid="apply-goals-input"
                    />
                  </div>

                  <div>
                    <label className="serif text-xl text-[#15243D] mb-3 block leading-snug" style={{ fontWeight: 300 }}>
                      Why now?
                    </label>
                    <p className="text-sm font-light text-[#8A8D93] mb-3">
                      What’s making this moment the moment.
                    </p>
                    <textarea
                      required
                      rows={4}
                      value={form.why_now}
                      onChange={update("why_now")}
                      className="anchor-input"
                      data-testid="apply-whynow-input"
                    />
                  </div>
                </div>

                {/* Commitment */}
                <div className="space-y-6">
                  <p className="overline">Section III — Commitment</p>
                  <p className="text-base font-light text-[#5A5D63]">
                    Where are you right now in your readiness?
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#15243D]/10">
                    {COMMITMENT_OPTIONS.map((opt) => (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => setForm({ ...form, commitment_level: opt.value })}
                        data-testid={`apply-commitment-${opt.value}`}
                        className={`p-6 text-left transition-colors duration-300 ${
                          form.commitment_level === opt.value
                            ? "bg-[#15243D] text-[#F7F5F0]"
                            : "bg-[#F7F5F0] text-[#15243D] hover:bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs tracking-[0.18em] uppercase">
                            {opt.value}
                          </span>
                          {form.commitment_level === opt.value && (
                            <Check size={16} strokeWidth={1.5} />
                          )}
                        </div>
                        <p className="serif text-xl" style={{ fontWeight: 300 }}>
                          {opt.label}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Referral */}
                <div>
                  <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                    How did you find Anchor? <span className="text-[#8A8D93] normal-case tracking-normal">(optional)</span>
                  </label>
                  <input
                    value={form.referral_source}
                    onChange={update("referral_source")}
                    className="anchor-input"
                    data-testid="apply-referral-input"
                  />
                </div>

                <div className="pt-8 border-t border-[#15243D]/10">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                    data-testid="apply-submit-button"
                  >
                    {loading ? "Sending…" : "Submit application"}
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </button>
                  <p className="mt-6 text-xs text-[#8A8D93] leading-relaxed max-w-md">
                    Your responses are private and read only by the coach. I’ll
                    reply within three days.
                  </p>
                </div>
              </form>
            </>
          ) : (
            <div data-testid="apply-success" className="max-w-2xl">
              <p className="overline mb-6">Received</p>
              <h1 className="serif text-5xl md:text-6xl text-[#15243D] leading-[1.05] mb-8" style={{ fontWeight: 300 }}>
                Thank you,<br />
                <em className="italic text-[#C07865]">{form.name || "friend"}</em>.
              </h1>
              <p className="text-lg font-light leading-relaxed text-[#5A5D63] mb-12">
                Your application has been received. I’ll read it carefully and
                respond within three days at the email you provided.
              </p>

              <div className="mb-12">
                <CalendlyCTA
                  testId="apply-calendly"
                  label="Or — book a discovery call now."
                  subtitle="If you’d like to move forward, you can choose a time directly. A quiet 30 minutes — no pressure, no pitch."
                />
              </div>

              <p className="text-base font-light text-[#5A5D63] mb-12">
                In the meantime, if you haven’t already, the{" "}
                <Link to="/program" className="text-[#C07865] hover:text-[#A66352]" data-testid="apply-program-link">
                  28-day program
                </Link>{" "}
                is a gentle way to begin.
              </p>
              <Link to="/" className="btn-secondary" data-testid="apply-home-button">
                Return home
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
